import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Check, Clock, MapPin, Timer, Loader2, XCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { t, slotTime } from "@/i18n";

const HERO =
  "https://images.unsplash.com/photo-1599074681549-902b476320d2?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200";

const Row = ({ icon, label, value }) => (
  <div className="flex items-center justify-between">
    <span className="flex items-center gap-2 text-coastal-muted text-sm">{icon} {label}</span>
    <span className="font-semibold text-coastal-text">{value}</span>
  </div>
);

const ConfirmBooking = () => {
  const navigate = useNavigate();
  const [status, setStatus] = useState("loading"); // loading | ok | error
  const [data, setData] = useState(null);
  const [lang, setLang] = useState("es");

  useEffect(() => {
    const token = new URLSearchParams(window.location.search).get("token");
    if (!token) {
      setStatus("error");
      return;
    }
    api
      .get(`/bookings/confirm?token=${encodeURIComponent(token)}`)
      .then((r) => {
        setData(r.data);
        setLang(r.data.language || "es");
        setStatus("ok");
      })
      .catch(() => setStatus("error"));
  }, []);

  const L = t[lang];

  return (
    <div className="min-h-screen flex flex-col lg:flex-row relative z-10">
      <div className="relative lg:w-[42%] h-40 lg:h-screen overflow-hidden bg-coastal-text">
        <img src={HERO} alt="Torrevieja" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t lg:bg-gradient-to-r from-coastal-text/70 via-coastal-text/20 to-transparent" />
        <div className="absolute bottom-0 left-0 p-8 lg:p-12">
          <h1 className="font-heading text-white text-3xl lg:text-5xl leading-tight drop-shadow">
            Free Tour Torrevieja
          </h1>
        </div>
      </div>

      <div className="flex-1 flex items-center justify-center p-6 sm:p-10 lg:p-16">
        {status === "loading" && (
          <div className="text-center" data-testid="confirm-loading">
            <Loader2 className="mx-auto text-coastal-primary animate-spin mb-4" size={40} />
            <p className="text-coastal-muted text-lg">{L.confirmingTitle}</p>
          </div>
        )}

        {status === "error" && (
          <div className="text-center max-w-md" data-testid="confirm-error">
            <XCircle className="mx-auto text-coastal-accent mb-4" size={48} />
            <h2 className="font-heading text-3xl text-coastal-text mb-3">{L.confirmError}</h2>
            <Button
              onClick={() => navigate("/")}
              className="rounded-full bg-coastal-primary text-white mt-4"
              data-testid="confirm-home-btn"
            >
              {L.goHome}
            </Button>
          </div>
        )}

        {status === "ok" && data && (
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="w-full max-w-lg text-center"
            data-testid="confirm-success"
          >
            <div className="mx-auto w-16 h-16 rounded-full bg-coastal-primary flex items-center justify-center mb-6">
              <Check className="text-white" size={32} />
            </div>
            <h2 className="font-heading text-4xl text-coastal-text mb-4">{L.confTitle}</h2>
            <p className="text-coastal-muted text-lg leading-relaxed mb-8">{L.confText}</p>

            <div className="bg-white rounded-2xl border border-coastal-secondary p-6 text-left space-y-4 shadow-[0_8px_32px_rgba(0,0,0,0.06)]">
              <Row icon={<Clock size={18} />} label={L.dateLabel} value={data.date} />
              <Row
                icon={<Clock size={18} />}
                label={L.timeLabel}
                value={slotTime(data.slot)}
              />
              <Row icon={<MapPin size={18} />} label={L.meetingPoint} value={L.meetingValue} />
              <Row icon={<Timer size={18} />} label={L.duration} value={L.durationValue} />
            </div>

            <Button
              onClick={() => navigate("/")}
              variant="outline"
              className="rounded-full mt-8 border-coastal-secondary text-coastal-text"
              data-testid="confirm-home-btn"
            >
              {L.goHome}
            </Button>
          </motion.div>
        )}
      </div>
    </div>
  );
};

export default ConfirmBooking;
